import { ConnectionRequestsScreen } from './routes/ConnectionRequestsScreen.js'
import type { ApolloClient } from '@apollo/client'
import { Button, I18nProvider, ToastProvider, linkClasses, useTranslate } from '@athena/ui'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
  useParams,
  useRouterState,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useEffect } from 'react'

import { StudentLayout } from './app/StudentLayout.js'
import { PageMessage } from './app/PageMessage.js'
import { RouteOrientation } from './app/route-orientation.js'
import { StudentShell } from './app/StudentShell.js'
import { TutorLayout } from './app/TutorLayout.js'
import { MeDocument } from './graphql/generated/operations.js'
import { fromLanguageCode } from '@athena/i18n'

import {
  DEFAULT_LANGUAGE,
  isLanguage,
  languageFromPathname,
  setCurrentLanguage,
  type Language,
} from './lib/language.js'
import { AssignedListScreen } from './routes/AssignedListScreen.js'
import { AttemptReviewScreen } from './routes/AttemptReviewScreen.js'
import { AttemptScreen } from './routes/AttemptScreen.js'
import { HistoryScreen } from './routes/HistoryScreen.js'
import { InviteScreen } from './routes/InviteScreen.js'
import { DashboardScreen } from './routes/DashboardScreen.js'
import { PracticeSettingsScreen } from './routes/PracticeSettingsScreen.js'
import { NewPracticeScreen } from './routes/NewPracticeScreen.js'
import { PracticeScreen } from './routes/PracticeScreen.js'
import { PracticesScreen } from './routes/PracticesScreen.js'
import { ProgressScreen } from './routes/ProgressScreen.js'
import { safeReturnPath } from './lib/return-to.js'
import { SignInScreen } from './routes/SignInScreen.js'
import { StudentScreen } from './routes/StudentScreen.js'
import { StudentsScreen } from './routes/StudentsScreen.js'
import { SubscriptionScreen } from './routes/SubscriptionScreen.js'
import { OnboardingScreen } from './routes/OnboardingScreen.js'

/**
 * Routes declared in code rather than generated from the file tree.
 *
 * V1.md's monorepo sketch says "file routes", and the directory below still
 * reads as one, but the generated `routeTree.gen.ts` has to exist before `tsc`
 * runs and the router's generator ships only as a Vite plugin, with no binary
 * this repo could hang a `codegen` turbo task off. Generation that only
 * happens inside `vite dev` is generation that CI does not do. Writing the
 * tree out costs a dozen lines and keeps the type checker self-sufficient.
 *
 * ── The language prefix ──────────────────────────────────────────────────
 *
 * Every application route lives under `/$language`, so `/es/practices` and
 * `/en/practices` are the canonical URLs and there is no unprefixed one
 * (V1.md section 18). One route parameter, one layout, and no page below
 * it knows that localization happened — which is the point of doing it here
 * rather than in every screen.
 */

export interface RouterContext {
  /**
   * Apollo, so `beforeLoad` can ask which language a person prefers.
   *
   * Through the router's context rather than an imported singleton: the redirect
   * has to read the *same* cache the components read, or the first navigation
   * fires a second `me` query and can disagree with what the page then renders.
   */
  client: ApolloClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
})

/**
 * Which language a URL with no prefix should become.
 *
 * The signed-in person's stored preference, and Spanish when there is nobody
 * signed in or the request fails (V1.md section 18). Deliberately not
 * the browser's `Accept-Language`: section 18 asks for a fixed default, and a
 * browser-derived one would make the same link open in different languages for
 * two people.
 *
 * `cache-first`, so this costs one request per session rather than one per
 * language-less link. A failure is not an error screen: an unreachable API
 * should land the visitor on `/es`, where the layouts below already have
 * something to say about it.
 */
async function preferredLanguage(client: ApolloClient): Promise<Language> {
  try {
    const result = await client.query({ query: MeDocument, fetchPolicy: 'cache-first' })
    return fromLanguageCode(result.data?.me?.preferredLanguage)
  } catch {
    return DEFAULT_LANGUAGE
  }
}

/** The same location, prefixed. Query string and hash survive. */
function localized(language: Language, pathname: string, search: string, hash: string): string {
  return `/${language}${pathname === '/' ? '' : pathname}${search}${hash}`
}

/**
 * `/` — the only route that is not under a language.
 *
 * It exists to leave: a tutor who types the bare hostname is sent to `/es` or
 * `/en`, and a student to their own entry point one redirect later.
 */
const rootIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context, location }) => {
    throw redirect({
      href: localized(
        await preferredLanguage(context.client),
        '/',
        location.searchStr,
        location.hash,
      ),
      replace: true,
    })
  },
})

/**
 * Everything else, under its language.
 *
 * `beforeLoad` is where an unsupported prefix is turned into a supported one.
 * The rule reads oddly and is deliberately one rule rather than two: a first
 * segment that is not a language is treated as a *missing* language, so
 * `/learn` becomes `/es/learn` and `/fr` becomes `/es/fr`, which
 * matches no child and lands on the not-found screen. A
 * language-less path and a nonsense language are the same mistake — the URL
 * did not say which of two languages to use — and answering both by prefixing
 * is what keeps `/practices` a working link without also making `/fr` one.
 *
 * There is no loop: the redirect always produces a first segment that *is* a
 * language, so the second pass falls straight through.
 */
const languageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$language',
  beforeLoad: async ({ context, location, params }) => {
    if (isLanguage(params.language)) return

    throw redirect({
      href: localized(
        await preferredLanguage(context.client),
        location.pathname,
        location.searchStr,
        location.hash,
      ),
      replace: true,
    })
  },
  component: LanguageLayout,
})

/**
 * The one place the URL's language becomes the app's language.
 *
 * Everything below reads `t` from the provider, and the API is told the same
 * answer through `setCurrentLanguage`. That assignment happens in the render
 * body on purpose: it has to be true *before* any child mounts and starts a
 * query, and an effect runs after. It is a write of one of two constants to a
 * module variable, idempotent, and re-run on every render — not state.
 */
function LanguageLayout() {
  const { language } = useParams({ from: languageRoute.id })
  const resolved = isLanguage(language) ? language : DEFAULT_LANGUAGE

  setCurrentLanguage(resolved)

  return (
    <I18nProvider language={resolved}>
      {/* The one toast host, above every screen's outlet: a confirmation
          raised just before a navigation is still on screen after it. */}
      <ToastProvider>
        <RouteOrientation />
        <Outlet />
      </ToastProvider>
    </I18nProvider>
  )
}

/**
 * `?redirect=` is where a protected screen was sending the visitor, kept as a
 * language-less app path and validated here and again where it is used (see
 * `lib/return-to.ts`). `?error=` is a provider's code, mapped to a sentence
 * and never printed. Anything else is dropped.
 */
const signInRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/sign-in',
  component: SignInScreen,
  validateSearch: (search: Record<string, unknown>): { error?: string; redirect?: string } => {
    const redirect = safeReturnPath(search['redirect'])
    const error = typeof search['error'] === 'string' ? search['error'].slice(0, 64) : undefined
    return { ...(error ? { error } : {}), ...(redirect ? { redirect } : {}) }
  },
})

const inviteRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/invite',
  component: InviteScreen,
})

const onboardingRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/onboarding',
  component: OnboardingScreen,
})

/** Pathless: every route under it gets the shell and the tutor check. */
const tutorRoute = createRoute({
  getParentRoute: () => languageRoute,
  id: 'tutor',
  component: TutorLayout,
})

/**
 * The dashboard's filter lives in the URL for the same reason the practice
 * list's search does: a dashboard narrowed to one student is a link a tutor can
 * keep. Unparseable values are dropped rather than rejected — a stale link
 * should show the whole dashboard, not an error.
 */
const dashboardRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/',
  component: DashboardScreen,
  validateSearch: (
    search: Record<string, unknown>,
  ): { studentId?: string; practiceId?: string } => {
    const text = (key: string) => (typeof search[key] === 'string' ? search[key] : undefined)

    return {
      ...(text('studentId') ? { studentId: text('studentId')! } : {}),
      ...(text('practiceId') ? { practiceId: text('practiceId')! } : {}),
    }
  },
})

const practicesRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/practices',
  component: PracticesScreen,
  /**
   * Search and both category filters live in the URL, so a narrowed shelf is a
   * link a tutor can keep or share. Unparseable values are dropped rather than
   * rejected, the same way the dashboard's filters are: a stale link should
   * open the practice list, not an error.
   */
  validateSearch: (search: Record<string, unknown>): { q?: string; subjectId?: string } => {
    const text = (key: string) => {
      const value = search[key]
      return typeof value === 'string' && value.length > 0 ? value : undefined
    }

    const q = text('q')
    const subjectId = text('subjectId')

    return {
      ...(q ? { q } : {}),
      ...(subjectId ? { subjectId } : {}),
    }
  },
})

const practiceSettingsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/practices/settings',
  component: PracticeSettingsScreen,
})

const newPracticeRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/practices/new',
  component: NewPracticeScreen,
})

const practiceRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/practices/$practiceId',
  component: PracticeScreen,
})

const studentsRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/students',
  component: StudentsScreen,
})

const subscriptionRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/plan',
  component: SubscriptionScreen,
})

const studentDetailRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/students/$studentId',
  component: StudentScreen,
})

/**
 * The tutor's review of one finished attempt. Under the tutor route, so the
 * layout's tutor check runs before the screen asks for an answer key.
 */
const attemptReviewRoute = createRoute({
  getParentRoute: () => tutorRoute,
  path: '/attempts/$attemptId',
  component: AttemptReviewScreen,
})

/**
 * The student half. Pathless as well, so the density flag and the student
 * check wrap every screen under it, but with no shell of its own: the runner
 * has to be able to show a question and nothing else.
 */
const studentRoute = createRoute({
  getParentRoute: () => languageRoute,
  id: 'student',
  component: StudentLayout,
})

/** The screens that take the shell. The attempt runner is a sibling, not a child. */
const studentShellRoute = createRoute({
  getParentRoute: () => studentRoute,
  id: 'student-shell',
  component: StudentShell,
})

const learnRoute = createRoute({
  getParentRoute: () => studentShellRoute,
  path: '/learn',
  component: AssignedListScreen,
})

/** Under the shell: the summary over the ledger, not a practice to sit. */
const progressRoute = createRoute({
  getParentRoute: () => studentShellRoute,
  path: '/learn/progress',
  component: ProgressScreen,
})

/** Under the shell: history is a place to browse, not a practice to sit. */
const historyRoute = createRoute({
  getParentRoute: () => studentShellRoute,
  path: '/learn/history',
  component: HistoryScreen,
})

const connectionRequestsRoute = createRoute({
  getParentRoute: () => studentShellRoute,
  path: '/learn/requests',
  component: ConnectionRequestsScreen,
})

const attemptRoute = createRoute({
  getParentRoute: () => studentRoute,
  path: '/learn/attempt/$attemptId',
  component: AttemptScreen,
})

const routeTree = rootRoute.addChildren([
  rootIndexRoute,
  languageRoute.addChildren([
    signInRoute,
    inviteRoute,
    onboardingRoute,
    tutorRoute.addChildren([
      dashboardRoute,
      practicesRoute,
      practiceSettingsRoute,
      subscriptionRoute,
      newPracticeRoute,
      practiceRoute,
      studentsRoute,
      studentDetailRoute,
      attemptReviewRoute,
    ]),
    studentRoute.addChildren([
      studentShellRoute.addChildren([
        learnRoute,
        progressRoute,
        historyRoute,
        connectionRequestsRoute,
      ]),
      attemptRoute,
    ]),
  ]),
])

/**
 * A page that does not exist, in the language of the URL that asked for one.
 *
 * It reads the language off the pathname rather than from the route match,
 * because there may not be one: `/fr` is redirected to `/es/fr`, which matches
 * the language route and nothing under it, but a not-found can also be raised
 * before that. Its own provider for the same reason — the layout above it may
 * never have rendered.
 */
function NotFound() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const language = languageFromPathname(pathname) ?? DEFAULT_LANGUAGE

  return (
    <I18nProvider language={language}>
      <NotFoundBody language={language} />
    </I18nProvider>
  )
}

function NotFoundBody({ language }: { language: Language }) {
  const t = useTranslate()

  return (
    <PageMessage title={t('notFound.title')}>
      <Link
        to="/$language"
        params={{ language }}
        className={linkClasses({ size: 'md', layout: 'standalone' })}
      >
        {t('notFound.back')}
      </Link>
    </PageMessage>
  )
}

/**
 * A page that failed to render, in the language of its URL.
 *
 * The router's own error screen is English and offers to print the error's
 * message — a developer's words, on a tutor's or a student's screen. This says
 * what did not happen and what to do about it, and nothing about why: the
 * error goes to the console, where the person who can act on it looks. Its own
 * provider, like the not-found page, because the layout that would have
 * supplied one is what failed.
 */
function RouteError({ error }: ErrorComponentProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const language = languageFromPathname(pathname) ?? DEFAULT_LANGUAGE

  useEffect(() => {
    console.error(error)
  }, [error])

  return (
    <I18nProvider language={language}>
      <RouteErrorBody />
    </I18nProvider>
  )
}

function RouteErrorBody() {
  const t = useTranslate()

  return (
    <PageMessage title={t('appError.crashTitle')}>
      <p className="text-body text-text-secondary">{t('appError.crashBody')}</p>
      <Button variant="secondary" onClick={() => globalThis.location.reload()}>
        {t('appError.reload')}
      </Button>
    </PageMessage>
  )
}

export const router = createRouter({
  routeTree,
  // Supplied by <RouterProvider context={{ client }}> in main.tsx, which is
  // where the one client is built. Declared here so `beforeLoad` is typed.
  context: undefined!,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: RouteError,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
